import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { enrichGalleryFromJobber, type EnrichmentResult } from '../api/enrichGallery'

interface EnrichParams {
  jobId: string // Base64-encoded Jobber GraphQL ID
  franchise: string // Franchise region (will be mapped to hamilton/northyork)
}

/**
 * Mutation hook to enrich a gallery with job data from Jobber.
 *
 * Flow:
 * 1. Calls enrichGalleryFromJobber() API function
 * 2. API fetches job details via existing fetchJobLineItems() infrastructure
 * 3. API upserts data to gallery_jobber_details table
 * 4. On success, invalidates related queries to refresh UI
 *
 * @param galleryId - The gallery to enrich
 * @returns React Query mutation with isPending, mutate, etc.
 *
 * @example
 * ```tsx
 * const { mutate: enrich, isPending } = useEnrichGallery(galleryId)
 *
 * // With job linkage data from useGalleryJobLinkage
 * enrich({ jobId: linkage.job_id, franchise: linkage.franchise_region })
 * ```
 *
 * @see useGalleryJobLinkage - Hook to get job_id and franchise from existing images
 * @see enrichGalleryFromJobber - The underlying API function
 */
export function useEnrichGallery(galleryId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ jobId, franchise }: EnrichParams): Promise<EnrichmentResult> => {
      return enrichGalleryFromJobber(galleryId, jobId, franchise)
    },

    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: 'Gallery enriched',
          description: data.jobNumber
            ? `Loaded job details for Job #${data.jobNumber}`
            : 'Successfully loaded Jobber data',
        })
      } else {
        // This case happens when the API returns success: false
        toast({
          title: 'Enrichment failed',
          description: data.errorMessage || 'Could not load Jobber data',
          variant: 'destructive',
        })
      }

      // Invalidate enrichment query to refresh the panel
      queryClient.invalidateQueries({ queryKey: ['gallery-jobber-details', galleryId] })
      // Also invalidate gallery detail in case we want to show enrichment status there
      queryClient.invalidateQueries({ queryKey: ['gallery', galleryId] })
    },

    onError: (error) => {
      console.error('[useEnrichGallery] Error:', error)
      toast({
        title: 'Enrichment failed',
        description: error instanceof Error ? error.message : 'Unable to load Jobber data.',
        variant: 'destructive',
      })
    },
  })
}

export default useEnrichGallery
